import { supabaseSettingsService } from "@/services/settingsService";

export type MasonryStyle = "rounded" | "classic";

export interface FeatureFlags {
  frame_enabled: boolean;
  social_enabled: boolean;
  charge_download: boolean;
  nfc_enabled: boolean;
  photo_wall_enabled: boolean;
  voice_guestbook_enabled: boolean;
  video_upload_enabled: boolean;
  qr_styles_enabled: boolean;
  image_upload_compression_enabled: boolean;
  local_thumbnail_variants_enabled: boolean;
  image_upload_store_original_enabled: boolean;
  image_upload_compression_quality: number;
  image_upload_compression_max_size_kb: number;
  superadmin_error_overlay_enabled: boolean;
  masonry_style: MasonryStyle;
  social_feed_v2: boolean;
  qr_deliverables: boolean;
  gallery_theme_switch_enabled: boolean;
  qr_deliverables_enabled: boolean;
  seat_finder_enabled: boolean;
  event_admin_seating_enabled: boolean;
  event_admin_rsvp_enabled: boolean;
  google_drive_enabled: boolean;
  scavenger_hunt_enabled: boolean;
  wedding_planner_enabled: boolean;
  // Upsell visibility flags (all default true)
  upsell_social: boolean;
  upsell_voice_guestbook: boolean;
  upsell_zip_download: boolean;
  upsell_video_upload: boolean;
  upsell_photo_wall: boolean;
  upsell_frame: boolean;
  upsell_qr_template: boolean;
  upsell_rsvp_planner: boolean;
  upsell_scavenger_hunt: boolean;
  upsell_photo_upgrades: boolean;
  upsell_retention_upgrades: boolean;
  // Unlimited photos (fair use) mode — hides count slider, locks to unlimited tier
  photo_fair_use: boolean;
  // Per-tier upsell toggles
  upsell_photo_100: boolean;
  upsell_photo_500: boolean;
  upsell_photo_1000: boolean;
  upsell_photo_2000: boolean;
  upsell_photo_unlimited: boolean;
  upsell_retention_14days: boolean;
  upsell_retention_month: boolean;
  upsell_retention_60days: boolean;
  upsell_retention_year: boolean;
  upsell_retention_2year: boolean;
  upsell_retention_5year: boolean;
}

const DEFAULTS: FeatureFlags = {
  frame_enabled: true,
  social_enabled: true,
  charge_download: false,
  nfc_enabled: true,
  photo_wall_enabled: true,
  voice_guestbook_enabled: true,
  video_upload_enabled: true,
  qr_styles_enabled: false,
  image_upload_compression_enabled: false,
  local_thumbnail_variants_enabled: false,
  image_upload_store_original_enabled: true,
  image_upload_compression_quality: 0.88,
  image_upload_compression_max_size_kb: 3072,
  superadmin_error_overlay_enabled: false,
  masonry_style: "rounded",
  social_feed_v2: false,
  gallery_theme_switch_enabled: false,
  qr_deliverables_enabled: false,
  seat_finder_enabled: false,
  event_admin_seating_enabled: false,
  event_admin_rsvp_enabled: false,
  google_drive_enabled: false,
  scavenger_hunt_enabled: false,
  wedding_planner_enabled: false,
  upsell_social: true,
  upsell_voice_guestbook: true,
  upsell_zip_download: true,
  upsell_video_upload: true,
  upsell_photo_wall: true,
  upsell_frame: true,
  upsell_qr_template: true,
  upsell_rsvp_planner: true,
  upsell_scavenger_hunt: true,
  upsell_photo_upgrades: true,
  upsell_retention_upgrades: true,
  photo_fair_use: false,
  upsell_photo_100: true,
  upsell_photo_500: true,
  upsell_photo_1000: true,
  upsell_photo_2000: true,
  upsell_photo_unlimited: true,
  upsell_retention_14days: true,
  upsell_retention_month: true,
  upsell_retention_60days: true,
  upsell_retention_year: true,
  upsell_retention_2year: true,
  upsell_retention_5year: true,
};

const FF_PREFIX = "ff_";

export async function loadFeatureFlags(): Promise<FeatureFlags> {
  const keys = Object.keys(DEFAULTS).map(k => `${FF_PREFIX}${k}`);
  const values = await supabaseSettingsService.getSettings(keys);

  const flags = { ...DEFAULTS };
  for (const [keyWithPrefix, value] of Object.entries(values)) {
      const row = { key: keyWithPrefix, value };
      const key = row.key.replace(FF_PREFIX, "") as keyof FeatureFlags;
      if (key in flags) {
        if (key === "masonry_style") {
          flags[key] = (row.value === "classic" ? "classic" : "rounded") as MasonryStyle;
        } else if (key === "image_upload_compression_quality") {
          const parsed = Number(row.value);
          flags[key] = (Number.isFinite(parsed) ? Math.min(1, Math.max(0.5, parsed)) : DEFAULTS.image_upload_compression_quality) as number;
        } else if (key === "image_upload_compression_max_size_kb") {
          const parsed = Number(row.value);
          flags[key] = (Number.isFinite(parsed) && parsed >= 100 ? Math.round(parsed) : DEFAULTS.image_upload_compression_max_size_kb) as number;
        } else {
          (flags as Record<keyof FeatureFlags, boolean | MasonryStyle | number>)[key] = row.value === "true";
        }
      }
    }
  return flags;
}

export async function saveFeatureFlags(flags: FeatureFlags): Promise<void> {
  const entries = Object.entries(flags).map(([key, value]) => ({
    key: `${FF_PREFIX}${key}`,
    value: String(value),
  }));

  for (const entry of entries) {
    await supabaseSettingsService.upsertSetting(entry.key, entry.value);
  }
}
